<template>
    <div class="container-fluid mt-45">
        <div class="row">
            <div class="col-md-6">
                <Breadcrumbs />
            </div>

            <div class="col-md-6 d-flex align-items-center justify-content-end">
                <div v-if="tokenExpirySeconds != null && tokenExpirySeconds > 0">
                    <p class="font-weight-bold mb-0">Time remaining {{ tokenExpirySeconds | moment('mm:ss') }} <img id="tooltip-target" class="ml-1" src="@/assets/images/icons/tooltip.svg" width="21" height="21"></p>
                    <b-tooltip target="tooltip-target" triggers="hover">
                        <div class="p-3">
                            <div class="d-flex align-items-center">
                                <img src="@/assets/images/icons/time-icon.svg" width="21" height="21" />
                                <div style="font-size: 1.1rem" class="ml-2">What's this?</div>
                            </div>
                            <div class="d-flex justify-content-start text-left mt-2">
                                This timer indicates how long your session will remain active to assign the device. If it expires, you'll be redirected to the dashboard, and the assignment process will need to be repeated
                            </div>
                        </div>
                    </b-tooltip>
                </div>
                <button @click="cancelTest()" class="btn btn-danger btn-cancel-test btn-block-mobile btn-lg lg-shadow ml-2 mt-4 mt-md-0">Cancel test</button>
            </div>
        </div>

        <div v-if="!error">
            <div class="row justify-content-center mt-5">
                <div class="col-md-8 d-flex flex-column justify-content-center align-items-center">
                    <h1>Assigning A Test Kit Using Your Phone</h1>
                </div>
            </div>

            <div class="row justify-content-center mt-4">
                <div class="col-md-4">
                    <div class="card shadow rounded border-0">
                        <div class="text-center py-4">
                            <img src="@/assets/images/icons/scan-icon.svg" class="img-fluid" />
                            <p class="h4">Assigning Test Kit</p>
                            <p class="mb-0">Scan the GTT@home test kit using your phone</p>
                        </div>

                        <hr class="py-2 my-0" />

                        <div class="card-body">
                            <div class="row">
                                <div class="col-md-4"><span class="font-weight-bold">Patient name</span></div>
                                <div class="col-md-4">{{ tokenResponse.patientGivenName }} {{ tokenResponse.patientFamilyName }}</div>
                            </div>

                            <hr/>

                            <div class="row">
                                <div class="col-md-4"><span class="font-weight-bold">{{ patientIdentifierLabel }}</span></div>
                                <div class="col-md-4">{{ tokenResponse.patientIdentifier }}</div>
                            </div>

                            <div class="text-center mt-4">
                                <img src="@/assets/images/scanning-animation.gif" width="250" class="img-fluid" />
                            </div>

                            <p class="text-center mt-4">Awaiting scan upload...</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else>
            <div class="row justify-content-center mt-5">
                <div class="col-md-8 d-flex flex-column justify-content-center align-items-center">
                    <h1 v-if="errorType == HCPAssignmentMessageType.DEVICE_ALREADY_ASSIGNED || errorType == HCPAssignmentMessageType.DEVICE_ALREADY_ASSIGNED_TO_OTHER_ORGANISATION">Assignment Conflict</h1>
                    <h1 v-else-if="errorType == HCPAssignmentMessageType.DEVICE_FAILED_CHECKS">Test Kit Problem</h1>
                    <h1 v-else>Assignment Error</h1>
                </div>
            </div>

            <div class="row justify-content-center mt-5">
                <div class="col-md-4">
                    <div class="card card-body shadow rounded border-0">
                        <div class="text-center py-4">
                            <img src="@/assets/images/icons/warning-icon.svg" width="60" class="img-fluid mb-4" />
                            <p v-if="errorType == HCPAssignmentMessageType.DEVICE_ALREADY_ASSIGNED" class="h4 mb-0">The device inside the Test Kit is already assigned to another patient</p>
                            <p v-else-if="errorType == HCPAssignmentMessageType.DEVICE_ALREADY_ASSIGNED_TO_OTHER_ORGANISATION" class="h4 mb-0">The device inside the Test Kit is already assigned with another organisation</p>
                            <p v-else-if="errorType == HCPAssignmentMessageType.DEVICE_FAILED_CHECKS" class="h4 mb-0">This device appears to be faulty. Please use another device.</p>
                            <p v-else class="h4 mb-0">Sorry, something went wrong</p>

                            <p v-if="errorType == HCPAssignmentMessageType.DEVICE_FAILED_CHECKS && tokenResponse && tokenResponse.deviceFaultCode" class="mt-3 mb-0">Error code: <span class="font-weight-bold">{{ tokenResponse.deviceFaultCode }}</span></p>
                        </div>

                        <div v-if="errorType == HCPAssignmentMessageType.DEVICE_ALREADY_ASSIGNED && tokenResponse != null && typeof tokenResponse === 'object' && Object.keys(tokenResponse).length">
                            <div v-if="tokenResponse.patientName">
                                <div class="row">
                                    <div class="col-md-4"><span class="font-weight-bold">Patient name</span></div>
                                    <div class="col-md-4">{{ tokenResponse.patientName }}</div>
                                </div>

                                <hr/>
                            </div>

                            <div v-if="tokenResponse.patientIdentifier">
                                <div class="row">
                                    <div class="col-md-4"><span class="font-weight-bold">NHS no</span></div>
                                    <div class="col-md-4">{{ tokenResponse.patientIdentifier }}</div>
                                </div>

                                <hr/>
                            </div>

                            <div v-if="tokenResponse.facilityName">
                                <div class="row">
                                    <div class="col-md-4"><span class="font-weight-bold">Facility name</span></div>
                                    <div class="col-md-4">{{ tokenResponse.facilityName }}</div>
                                </div>

                                <hr/>
                            </div>

                            <div v-if="tokenResponse.testCreatedDate">
                                <div class="row">
                                    <div class="col-md-4"><span class="font-weight-bold">Test created</span></div>
                                    <div class="col-md-4">{{ formatDate(tokenResponse.testCreatedDate) }}</div>
                                </div>

                                <hr/>
                            </div>

                            <div v-if="tokenResponse.testDate">
                                <div class="row">
                                    <div class="col-md-4"><span class="font-weight-bold">Test date</span></div>
                                    <div class="col-md-4">{{ formatDate(tokenResponse.testDate) }}</div>
                                </div>

                                <hr/>
                            </div>
                        </div>

                        <p class="text-center mt-4">Please scan another device using your phone...</p>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="manualEntryAllowed" class="manual-entry-corner">
            <button @click="manualEntryVisible = !manualEntryVisible" class="btn btn-link p-0 manual-entry-toggle" title="Tester tools" aria-label="Tester tools" :aria-expanded="manualEntryVisible.toString()" aria-controls="manual-entry-panel">&#9881;</button>

            <div v-show="manualEntryVisible" id="manual-entry-panel" class="card card-body shadow rounded border-0 mt-2 manual-entry-panel">
                <p class="mb-2"><small class="font-weight-bold">Tester only — {{ environmentName }}</small></p>
                <p id="manual-entry-help" class="mb-2"><small>Paste a device raw string to stand in for a phone scan. Not available on training or production.</small></p>
                <label for="manualRawString" class="mb-1"><small>Device raw string</small></label>
                <textarea id="manualRawString" v-model="manualRawString" rows="4" class="form-control mb-2" aria-describedby="manual-entry-help"></textarea>
                <button @click="submitManualRawString()" :disabled="manualEntrySubmitting || !manualRawString" class="btn btn-secondary">Submit raw string</button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import {mapActions, mapGetters} from 'vuex'
import Breadcrumbs from '@/partials/site-structure/BreadcrumbsComponent.vue'
import CancelTestModalComponent from '@/components/modals/CancelTestModalComponent.vue'
import ReassignDeviceModalComponent from '@/components/modals/ReassignDeviceModalComponent.vue'
import HCPAssignmentMode from '../../../Common/enums/HCPAssignmentMode'
import HCPAssignmentMessageType from '../../../Common/enums/HCPAssignmentMessageType'
import SignalRMixin from '@/mixins/SignalRMixin'
import FormatterMixin from '../../../Common/mixins/FormatterMixin'
import SessionExpiredModal from '@/components/modals/SessionExpiredModalComponent'
import ConnectionDiagnosticsModal from '@/components/modals/ConnectionDiagnosticsModalComponent'
import DeviceFaultModalComponent from '@/components/modals/DeviceFaultModalComponent.vue'

export default {
    name: 'AssignTestKit',

    mixins: [
        SignalRMixin,
        FormatterMixin
    ],

    components: {
        Breadcrumbs
    },

    data() {
        return {
            CancelTestModalComponent,
            ReassignDeviceModalComponent,
            SessionExpiredModal,
            DeviceFaultModalComponent,
            HCPAssignmentMode,
            HCPAssignmentMessageType,
            error: false,
            errorType: null,
            manualEntryVisible: false,
            manualEntrySubmitting: false,
            manualRawString: ''
        }
    },

    mounted() {
        if(!this.isSignalRConnected()) {
            return this.$router.push({name: 'Dashboard'})
        }

        this.getTokenExpiry()
        this.bindConnectionMessage(this.signalRConnection)
    },

    watch: {
        tokenExpirySeconds: function(val) {
            if(val != null && val <= 0) {
                this.$modal.show(SessionExpiredModal, {}, {width: '400px'})
                return this.$router.push({name: 'TestDetails'})
            } 
        }
    },

    computed: {
        ...mapGetters({
            tokenResponse: 'SignalRStore/GetTokenResponse',
            patientIdentifierLabel: 'OrganisationStore/GetPatientIdentifierLabel'
        }),

        environmentName() {
            return process.env.VUE_APP_ENV
        },

        // Developer and UAT builds only. The API refuses manual entry independently, so hiding this is
        // convenience rather than the control - training and production are excluded in both places.
        manualEntryAllowed() {
            return ['dev', 'dev-alpha', 'dev-vm', 'local', 'uat'].includes(process.env.VUE_APP_ENV)
        }
    },

    methods: {
        ...mapActions({
            setNotification: 'NotificationStore/SetNotification',
        }),

        cancelTest() {
            this.$modal.show(CancelTestModalComponent, {}, {width: '400px'});
        },

        bindConnectionMessage(connection) {
            var completeAssignmentCallback = function (tokenDetails, type) {
                if (!type) return
                this.error = false
                this.errorType = null
                this.setTokenResponse(tokenDetails)
                this.$router.push({name: 'Confirmation'})
            }.bind(this)

            var hcpAssignmentTokenErrorCallback = function (tokenDetails, type) {
                if (!type) return
                this.setTokenResponse(tokenDetails)
                this.error = true
                this.errorType = type

                if (type == HCPAssignmentMessageType.DEVICE_FAILED_CHECKS) {
                    this.$modal.show(DeviceFaultModalComponent, {code: tokenDetails && tokenDetails.deviceFaultCode}, {width: '400px'})
                }
            }.bind(this)

            connection.on("completeAssignment", completeAssignmentCallback)
            connection.on("hcpAssignmentTokenError", hcpAssignmentTokenErrorCallback)
        },

        // Stands in for the phone scan so a raw string can be exercised end to end on a test
        // environment. The outcome arrives over SignalR exactly as a real scan's would.
        async submitManualRawString() {
            this.manualEntrySubmitting = true

            try {
                await axios.post(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/completehcpassignment`, {
                    token: this.token,
                    rawString: this.manualRawString,
                    manualRawStringEntry: true
                })
            }
            catch(err) {
                this.setNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'The raw string could not be submitted'
                })
            }
            finally {
                this.manualEntrySubmitting = false
            }
        },

        // The connection died awaiting the scan upload; the device may still have been assigned
        // server-side, so the diagnostics modal explains and the user re-checks from TestDetails.
        onSignalRConnectionLost() {
            this.$modal.show(ConnectionDiagnosticsModal, {}, {width: '600px'})
            if(this.$route.name !== 'TestDetails') this.$router.push({name: 'TestDetails'})
        }
    },

    beforeDestroy() {
        this.stopTokenExpiryCountdown()
    }
}
</script>

<style scoped>
.manual-entry-corner {
    position: fixed;
    right: 1rem;
    /* Clears the ConnectionMethodBadge, which is fixed at bottom: 8px and ~17px tall. */
    bottom: 2rem;
    z-index: 10;
}

.manual-entry-toggle {
    opacity: 0.25;
    font-size: 1.25rem;
    line-height: 1;
}

.manual-entry-toggle:hover,
.manual-entry-toggle:focus {
    opacity: 1;
}

.manual-entry-panel {
    width: 360px;
    max-width: calc(100vw - 2rem);
}
</style>